import React, { useState, useEffect, useRef, useCallback } from 'react';
import PasswordDetail from './PasswordDetail';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { FiSearch, FiFilter, FiEye, FiEyeOff, FiEdit, FiTrash } from 'react-icons/fi';
import './PasswordList.css';

const PasswordList = ({ setMessage }) => {
  const [passwords, setPasswords] = useState([]);
  const [selectedPassword, setSelectedPassword] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const passwordsPerPage = 30;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [passwordToDelete, setPasswordToDelete] = useState(null);
  const filterMenuRef = useRef(null);

  const fetchPasswords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/getPasswords');
      const data = await response.json();
      setPasswords(data);
    } catch (error) {
      console.error('获取密码失败:', error);
      setMessage('获取密码失败');
    } finally {
      setLoading(false);
    }
  }, [setMessage]);

  useEffect(() => {
    fetchPasswords();
  }, [fetchPasswords]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);

  const handleDelete = (id) => {
    setPasswordToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    setShowDeleteConfirmation(false);
    try {
      const response = await fetch('/api/deletePassword', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: passwordToDelete }),
      });

      if (response.ok) {
        fetchPasswords();
        setMessage('密码删除成功');
      } else {
        setMessage('密码删除失败');
      }
    } catch (error) {
      setMessage('删除密码时出错');
    } finally {
      setLoading(false);
      setPasswordToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setPasswordToDelete(null);
  };

  const togglePasswordVisibility = (id) => {
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage(`${label}已复制到剪贴板`);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      setMessage(`复制失败: ${err}`);
    });
  };

  const copySiteToClipboard = (site) => {
    navigator.clipboard.writeText(site).then(() => {
      setMessage('网站名称已复制到剪贴板');
    }).catch(err => {
      console.error('Failed to copy site: ', err);
      setMessage(`复制失败: ${err}`);
    });
  };

  const filteredPasswords = passwords.filter(password => {
    const matchesCategory = categoryFilter ? password.category === categoryFilter : true;
    const matchesSearch = password.site.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         password.username.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['AI', '网站', '账号', '其他', '导入'];

  // 分页
  const indexOfLastPassword = currentPage * passwordsPerPage;
  const indexOfFirstPassword = indexOfLastPassword - passwordsPerPage;
  const currentPasswords = filteredPasswords.slice(indexOfFirstPassword, indexOfLastPassword);

  const totalPages = Math.ceil(filteredPasswords.length / passwordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="password-list">
      <div className="list-controls">
        <div className="search-bar">
          <FiSearch className="icon" size={18} />
          <input
            type="text"
            placeholder="搜索密码..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-button" onClick={() => setSearchTerm('')}>
              ×
            </button>
          )}
        </div>
        
        <div className="filter-container">
          <button 
            className="filter-button"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            <FiFilter className="icon" size={16} />
            <span>分类过滤</span>
          </button>
          
          {showFilterMenu && (
            <div className="filter-menu" ref={filterMenuRef}>
              <div
                className={`filter-option ${!categoryFilter ? 'active' : ''}`}
                onClick={() => {
                  setCategoryFilter('');
                  setShowFilterMenu(false)
                }}
              >
                全部
              </div>
              {categories.map(cat => (
                <div
                  key={cat}
                  className={`filter-option ${categoryFilter === cat ? 'active' : ''}`}
                  onClick={() => {
                    setCategoryFilter(cat);
                    setShowFilterMenu(false);
                  }}
                >
                  {cat}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-indicator">加载中...</div>
      ) : (
        <div className="password-cards">
          {currentPasswords.map(password => (
            <div key={password.id} className="password-card">
              <div className="card-header">
                <span className="category-badge">{password.category}</span>
                <span className="site-name" onClick={() => copySiteToClipboard(password.site)}>
                  {password.site}
                </span>
              </div>
              
              <div className="card-body">
                  <div className="info-item">
                  <label>用户名</label>
                  <div className="value-container" onClick={() => copyToClipboard(password.username, '用户名')}>
                    <p>{password.username}</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <label>密码</label>
                  <div className="value-container" onClick={() => copyToClipboard(password.password, '密码')}>
                    <span>{showPassword[password.id] ? password.password : '••••••••'}</span>
                    <div className="action-group">
                      <button
                        className="icon-button"
                        onClick={() => togglePasswordVisibility(password.id)}
                      >
                        {showPassword[password.id] ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-actions">
                <button 
                  className="edit-btn"
                  onClick={() => setSelectedPassword(password)}
                >
                  <FiEdit size={16} /> 编辑
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(password.id)}
                >
                  <FiTrash size={16} /> 删除
                </button>
              </div>
            </div>
          ))}
          
          {currentPasswords.length === 0 && (
            <div className="empty-state">
              <img src="https://img.liyao.sbs/file/1738502727198.png" alt="无密码" />
              <p>哪儿呢？</p>
            </div>
          )}
        </div>
      )}

      <div className="pagination">
        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
          上一页
        </button>
        <span>{currentPage} / {totalPages}</span>
        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
          下一页
        </button>
      </div>

      {selectedPassword && (
        <PasswordDetail
          password={selectedPassword}
          onClose={() => setSelectedPassword(null)}
          onPasswordUpdate={fetchPasswords}
        />
      )}

      <DeleteConfirmationModal
        visible={showDeleteConfirmation}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={loading}
      />
    </div>
  );
};

export default PasswordList;
